import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuthState";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import Navbar from "@/components/navbar";
import Footer from "@/components/footer";

import { 
  User, 
  Edit3, 
  Save, 
  X, 
  Calendar, 
  MapPin, 
  Phone, 
  Mail, 
  UserCheck,
  Shield,
  Bell,
  Heart,
  Globe,
  Camera,
  Trophy,
  BarChart3,
  CreditCard,
  AlertCircle
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { UpdateUserProfile, User as UserType } from "@shared/schema";

interface UserProfile extends UserType {
  // Additional fields for display
}

// South African ID number validation
function validateSouthAfricanID(idNumber: string): { isValid: boolean; error?: string } {
  // Remove any spaces or special characters
  const cleaned = idNumber.replace(/\D/g, '');
  
  // Must be exactly 13 digits
  if (cleaned.length !== 13) {
    return { isValid: false, error: 'South African ID number must be exactly 13 digits' };
  }

  // Extract components
  const year = parseInt(cleaned.substring(0, 2), 10);
  const month = parseInt(cleaned.substring(2, 4), 10);
  const day = parseInt(cleaned.substring(4, 6), 10);
  
  // Validate month (01-12)
  if (month < 1 || month > 12) {
    return { isValid: false, error: 'Invalid month in ID number' };
  }
  
  // Validate day (01-31)
  if (day < 1 || day > 31) {
    return { isValid: false, error: 'Invalid day in ID number' };
  }
  
  // Validate check digit using Luhn algorithm - Process from right to left
  const digits = cleaned.split('').map(Number);
  let sum = 0;
  
  // Process the first 12 digits from right to left (excluding the check digit)
  for (let i = 11; i >= 0; i--) {
    const position = 11 - i; // Position from the right (0-based)
    if (position % 2 === 0) {
      // Even positions from right: double the digit
      const doubled = digits[i] * 2;
      sum += doubled > 9 ? doubled - 9 : doubled;
    } else {
      // Odd positions from right: add as is
      sum += digits[i];
    }
  }
  
  const checkDigit = (10 - (sum % 10)) % 10;
  
  if (checkDigit !== digits[12]) {
    return { isValid: false, error: 'Invalid ID number - check digit verification failed' };
  }
  
  return { isValid: true };
}

export default function UserProfile() {
  const { toast } = useToast();
  const { user: authUser, loading } = useAuth();
  const isAuthenticated = !!authUser;
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<UpdateUserProfile>>({});
  const [idValidationError, setIdValidationError] = useState<string | null>(null);
  
  // Redirect to home if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = "/api/login";
      }, 500);
      return;
    }
  }, [isAuthenticated, loading, toast]);

  const { data: dashboardData, isLoading: profileLoading } = useQuery<any>({
    queryKey: ["/api/user/dashboard-optimized"],
    enabled: isAuthenticated,
  });

  // Extract profile from optimized dashboard data
  const profile = dashboardData?.profile;

  const updateProfileMutation = useMutation({
    mutationFn: async (updateData: Partial<UpdateUserProfile>) => {
      return await apiRequest("PUT", "/api/user/profile", { body: updateData });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user/dashboard-optimized"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      setIsEditing(false);
      toast({
        title: "Profile Updated",
        description: "Your profile has been successfully updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating profile",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Separate mutation for instant marketing consent updates
  const updateMarketingConsentMutation = useMutation({
    mutationFn: async (marketingConsent: boolean) => {
      return await apiRequest("PUT", "/api/user/profile", { body: { marketingConsent } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user/dashboard-optimized"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      toast({
        title: "Preference Updated",
        description: "Your marketing communication preference has been saved.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating preference",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing
      setIsEditing(false);
      setFormData({});
    } else {
      // Start editing
      setIsEditing(true);
      setFormData({
        firstName: profile?.firstName || "",
        lastName: profile?.lastName || "",
        phone: profile?.phone || "",
        location: profile?.location || "",
        interests: profile?.interests || "",
        gender: profile?.gender || "",
        dateOfBirth: profile?.dateOfBirth || "",
        idNumber: profile?.idNumber || "",
        marketingConsent: profile?.marketingConsent || false,
      });
    }
  };

  const handleInputChange = (field: keyof UpdateUserProfile, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Validate ID number if it's being changed
    if (field === 'idNumber' && typeof value === 'string') {
      if (value.trim() === '') {
        setIdValidationError(null);
      } else {
        const validation = validateSouthAfricanID(value);
        setIdValidationError(validation.isValid ? null : validation.error || null);
      }
    }
  };

  const handleSubmit = () => {
    // Validate ID number before submitting
    if (formData.idNumber && formData.idNumber.trim() !== '') {
      const validation = validateSouthAfricanID(formData.idNumber);
      if (!validation.isValid) {
        setIdValidationError(validation.error || 'Invalid ID number');
        toast({
          title: "Invalid ID Number",
          description: validation.error || 'Please enter a valid South African ID number',
          variant: "destructive",
        });
        return;
      }
    }

    updateProfileMutation.mutate(formData);
  };

  if (loading || profileLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading profile...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect
  }

  // Use correct statistics from dashboard-optimized response
  const totalEntries = dashboardData?.stats?.totalEntries || 0;
  const totalCompetitions = dashboardData?.stats?.competitionsEntered || 0;
  const activeCompetitions = dashboardData?.stats?.liveCompetitions || 0;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />
      
      {/* Profile Header - Mobile Responsive */}
      <section className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-8 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-4 sm:mb-6">
              <div className="relative">
                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  {profile?.profileImageUrl ? (
                    <img 
                      src={profile.profileImageUrl} 
                      alt="Profile" 
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <User className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
                  )}
                </div>
                <button className="absolute bottom-0 right-0 w-6 h-6 sm:w-8 sm:h-8 bg-primary rounded-full flex items-center justify-center text-white hover:bg-primary/90 transition-colors">
                  <Camera className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
              </div>
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-2 sm:mb-3 leading-tight px-4">
              {profile?.firstName && profile?.lastName 
                ? `${profile.firstName} ${profile.lastName}`
                : profile?.username || authUser?.email?.split('@')[0]
              }
            </h1>
            {profile?.username && (profile?.firstName || profile?.lastName) && (
              <p className="text-base sm:text-lg text-indigo-200 mb-3 sm:mb-4 truncate px-4" data-testid="text-username-display">
                <span className="text-sm text-indigo-300">Username: </span>
                {profile.username}
              </p>
            )}
            <p className="text-base sm:text-lg lg:text-xl text-indigo-100 mb-3 sm:mb-4 truncate px-4">
              {profile?.email}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-4 mb-4 sm:mb-6 px-4">
              <Badge variant="secondary" className="text-xs sm:text-sm">
                <Shield className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                {authUser?.role === 'admin' ? 'Administrator' : 'Member'}
              </Badge>
              <Badge variant="outline" className="text-xs sm:text-sm text-white border-white">
                <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                Joined {new Date(profile?.createdAt || '').toLocaleDateString()}
              </Badge>
            </div>
            <div className="flex justify-center space-x-8 lg:hidden">
              <div className="text-center">
                <div className="text-2xl font-bold">{totalCompetitions}</div>
                <div className="text-sm text-indigo-100">Competitions</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{totalEntries}</div>
                <div className="text-sm text-indigo-100">Total Entries</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{activeCompetitions}</div>
                <div className="text-sm text-indigo-100">Active</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Profile Content - Mobile Responsive */}
      <section className="py-8 sm:py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <Tabs defaultValue="personal" className="w-full">
            <TabsList className="grid w-full grid-cols-2 h-auto">
              <TabsTrigger value="personal" className="text-xs sm:text-sm py-2">Personal Info</TabsTrigger>
              <TabsTrigger value="preferences" className="text-xs sm:text-sm py-2">Preferences</TabsTrigger>
            </TabsList>
            
            <TabsContent value="personal" className="mt-4 sm:mt-6">
              <Card>
                <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
                  <CardTitle className="flex items-center text-base sm:text-lg">
                    <User className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                    Personal Information
                  </CardTitle>
                  <Button 
                    variant={isEditing ? "outline" : "default"} 
                    onClick={handleEditToggle}
                    disabled={updateProfileMutation.isPending}
                    size="sm"
                    className="w-full sm:w-auto"
                  >
                    {isEditing ? (
                      <>
                        <X className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="text-xs sm:text-sm">Cancel</span>
                      </>
                    ) : (
                      <>
                        <Edit3 className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="text-xs sm:text-sm">Edit Profile</span>
                      </>
                    )}
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4 sm:space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <Label htmlFor="firstName">First Name</Label>
                      {isEditing ? (
                        <Input
                          id="firstName"
                          value={formData.firstName || profile?.firstName || ""}
                          onChange={(e) => handleInputChange("firstName", e.target.value)}
                          placeholder="Enter your first name"
                        />
                      ) : (
                        <p className="mt-1 text-sm text-gray-600">
                          {profile?.firstName || "Not provided"}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="lastName">Last Name</Label>
                      {isEditing ? (
                        <Input
                          id="lastName"
                          value={formData.lastName || profile?.lastName || ""}
                          onChange={(e) => handleInputChange("lastName", e.target.value)}
                          placeholder="Enter your last name"
                        />
                      ) : (
                        <p className="mt-1 text-sm text-gray-600">
                          {profile?.lastName || "Not provided"}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email">Email Address</Label>
                      <div className="flex items-center mt-1">
                        <Mail className="w-4 h-4 text-gray-400 mr-2" />
                        <p className="text-sm text-gray-600">{profile?.email}</p>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      {isEditing ? (
                        <Input
                          id="phone"
                          value={formData.phone || ""}
                          onChange={(e) => handleInputChange("phone", e.target.value)}
                          placeholder="27821234567"
                        />
                      ) : (
                        <div className="flex items-center mt-1">
                          <Phone className="w-4 h-4 text-gray-400 mr-2" />
                          <p className="text-sm text-gray-600">
                            {profile?.phone || "Not provided"}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="dateOfBirth">Date of Birth</Label>
                      {isEditing ? (
                        <Input
                          id="dateOfBirth"
                          type="date"
                          value={formData.dateOfBirth || ""}
                          onChange={(e) => handleInputChange("dateOfBirth", e.target.value)}
                        />
                      ) : (
                        <div className="flex items-center mt-1">
                          <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                          <p className="text-sm text-gray-600">
                            {profile?.dateOfBirth 
                              ? new Date(profile.dateOfBirth).toLocaleDateString()
                              : "Not provided"
                            }
                          </p>
                        </div>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="gender">Gender</Label>
                      {isEditing ? (
                        <Select 
                          value={formData.gender || ""} 
                          onValueChange={(value) => handleInputChange("gender", value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select gender" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                            <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="mt-1 text-sm text-gray-600">
                          {profile?.gender || "Not provided"}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="location">Location</Label>
                    {isEditing ? (
                      <Input
                        id="location"
                        value={formData.location || ""}
                        onChange={(e) => handleInputChange("location", e.target.value)}
                        placeholder="Enter your location"
                      />
                    ) : (
                      <div className="flex items-center mt-1">
                        <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                        <p className="text-sm text-gray-600">
                          {profile?.location || "Not provided"}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Temporarily hidden - ID Number field
                  <div>
                    <Label htmlFor="idNumber">ID Number</Label>
                    {isEditing ? (
                      <div className="space-y-2">
                        <Input
                          id="idNumber"
                          value={formData.idNumber || ""}
                          onChange={(e) => handleInputChange("idNumber", e.target.value)}
                          placeholder="Enter your South African ID number"
                          className={idValidationError ? 'border-red-500 focus:border-red-500' : ''}
                          data-testid="input-id-number"
                        />
                        {idValidationError && (
                          <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{idValidationError}</AlertDescription>
                          </Alert>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center mt-1">
                        <CreditCard className="w-4 h-4 text-gray-400 mr-2" />
                        <p className="text-sm text-gray-600">
                          {profile?.idNumber ? "***" + profile.idNumber.slice(-4) : "Not provided"}
                        </p>
                      </div>
                    )}
                  </div>
                  */}

                  <div>
                    <Label htmlFor="interests">Interests</Label>
                    {isEditing ? (
                      <Textarea
                        id="interests"
                        value={formData.interests || ""}
                        onChange={(e) => handleInputChange("interests", e.target.value)}
                        placeholder="Tell us about your interests..."
                        rows={3}
                      />
                    ) : (
                      <div className="flex items-start mt-1">
                        <Heart className="w-4 h-4 text-gray-400 mr-2 mt-1" />
                        <p className="text-sm text-gray-600">
                          {profile?.interests || "Not provided"}
                        </p>
                      </div>
                    )}
                  </div>

                  {isEditing && (
                    <div className="flex justify-end space-x-2 pt-4">
                      <Button 
                        variant="outline" 
                        onClick={handleEditToggle}
                        disabled={updateProfileMutation.isPending}
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleSubmit}
                        disabled={updateProfileMutation.isPending}
                      >
                        {updateProfileMutation.isPending ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        ) : (
                          <Save className="mr-2 h-4 w-4" />
                        )}
                        Save Changes
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="preferences" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Bell className="mr-2 h-5 w-5" />
                    Preferences & Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="marketingConsent">Marketing Communications</Label>
                      <p className="text-sm text-gray-600">
                        Receive updates about new competitions and promotions
                      </p>
                    </div>
                    <Switch
                      id="marketingConsent"
                      checked={profile?.marketingConsent || false}
                      onCheckedChange={(checked) => {
                        updateMarketingConsentMutation.mutate(checked);
                      }}
                      disabled={updateMarketingConsentMutation.isPending}
                      data-testid="switch-marketing-consent"
                    />
                  </div>
                  
                  <div className="border-t pt-4">
                    <h3 className="font-medium mb-2">Account Status</h3>
                    <div className="flex items-center space-x-2">
                      <UserCheck className="w-4 h-4 text-green-500" />
                      <Badge variant="outline" className="text-green-600 border-green-600">
                        Active Account
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

            </TabsContent>


          </Tabs>
        </div>
      </section>

      <Footer />
    </div>
  );
}
